import { useParams, Link } from 'react-router-dom';
import {
  ArrowLeft, MapPin, Users, DollarSign, Church, GraduationCap,
  ShoppingCart, Building2, Globe, Clock, Car, Plug, Phone,
  Lightbulb, AlertTriangle, ExternalLink, Home, Stethoscope,
  Languages, CreditCard
} from 'lucide-react';
import { Badge } from '@/components/common';
import { getCountryGuide } from '@/data/countryGuides';
import SEO from '@/components/common/SEO';
import { ThinPage } from '@/components/common/PageQuality';

export default function CountryDetail() {
  const { countryCode } = useParams<{ countryCode: string }>();
  const guide = getCountryGuide(countryCode || '');

  if (!guide) {
    return (
      <>
        <SEO title="Country guide not found" description="This country guide does not exist." noindex />
        <ThinPage reason="unknown country code" />
      <div className="min-h-screen bg-gray-50 dark:bg-gray-900 flex items-center justify-center">
        <div className="text-center">
          <h1 className="text-2xl font-bold text-gray-900 dark:text-white mb-2">
            Country Not Found
          </h1>
          <p className="text-gray-500 dark:text-gray-400 mb-4">
            We don't have a guide for this country yet.
          </p>
          <Link
            to="/countries"
            className="inline-flex items-center gap-2 text-emerald-600 hover:text-emerald-700"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Country Guides
          </Link>
        </div>
      </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <SEO
        title={`Moving to ${guide.name}: Visa, Cost of Living & Korean Community Guide`}
        description={`A relocation guide to ${guide.name} for Koreans abroad — visa routes, cost of living, and where the Korean community is.`}
        path={`/countries/${guide.code.toLowerCase()}`}
      />
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-emerald-600 to-teal-600 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
          <Link
            to="/countries"
            className="inline-flex items-center gap-1 text-emerald-100 hover:text-white mb-4 text-sm"
          >
            <ArrowLeft className="w-4 h-4" />
            All Countries
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-5xl sm:text-6xl">{guide.flag}</span>
            <div>
              <h1 className="text-2xl sm:text-4xl font-bold">{guide.name}</h1>
              <p className="text-emerald-100 text-lg">{guide.nameKo}</p>
            </div>
          </div>
          <div className="mt-4 flex items-center gap-4 text-emerald-100">
            <div className="flex items-center gap-2">
              <Users className="w-5 h-5" />
              <span>{guide.koreanPopulation} Koreans</span>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Main Content */}
          <div className="lg:col-span-2 space-y-8">
            {/* Overview */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Globe className="w-5 h-5 text-emerald-500" />
                Overview
              </h2>
              <p className="text-gray-600 dark:text-gray-300 leading-relaxed">
                {guide.overview}
              </p>
              <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 italic">
                {guide.koreanPopulationNote}
              </p>
            </section>

            {/* Major Cities */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <MapPin className="w-5 h-5 text-emerald-500" />
                Major Korean Communities
              </h2>
              <div className="space-y-4">
                {guide.majorCities.map((city) => (
                  <div
                    key={city.name}
                    className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg"
                  >
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h3 className="font-semibold text-gray-900 dark:text-white">
                          {city.name}
                        </h3>
                        {city.koreanNeighborhood && (
                          <p className="text-sm text-emerald-600 dark:text-emerald-400">
                            Korean area: {city.koreanNeighborhood}
                          </p>
                        )}
                      </div>
                      <Badge variant="green" size="sm">
                        <Users className="w-3 h-3 mr-1" />
                        {city.koreanPopulation}
                      </Badge>
                    </div>
                    <div className="flex flex-wrap gap-1 mt-2">
                      {city.highlights.map((highlight, idx) => (
                        <Badge key={idx} variant="gray" size="sm">{highlight}</Badge>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </section>

            {/* Cost of Living */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <DollarSign className="w-5 h-5 text-emerald-500" />
                Cost of Living
              </h2>
              <div className="grid sm:grid-cols-2 gap-4">
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <Home className="w-4 h-4" />
                    <span className="text-sm">Rent (1BR)</span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {guide.costOfLiving.rentOneBedroom}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <ShoppingCart className="w-4 h-4" />
                    <span className="text-sm">Groceries (Monthly)</span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {guide.costOfLiving.groceriesMonthly}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <Car className="w-4 h-4" />
                    <span className="text-sm">Public Transport</span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {guide.costOfLiving.publicTransport}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 text-gray-500 dark:text-gray-400 mb-1">
                    <DollarSign className="w-4 h-4" />
                    <span className="text-sm">Meal Out</span>
                  </div>
                  <p className="font-semibold text-gray-900 dark:text-white">
                    {guide.costOfLiving.mealOut}
                  </p>
                </div>
              </div>
              {guide.costOfLiving.notes && (
                <p className="mt-4 text-sm text-gray-500 dark:text-gray-400 bg-amber-50 dark:bg-amber-900/20 p-3 rounded-lg">
                  <Lightbulb className="w-4 h-4 inline mr-1 text-amber-500" />
                  {guide.costOfLiving.notes}
                </p>
              )}
            </section>

            {/* Korean Community */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Users className="w-5 h-5 text-emerald-500" />
                Korean Community Resources
              </h2>
              <div className="grid sm:grid-cols-2 gap-4 mb-4">
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Church className="w-5 h-5 text-purple-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Churches</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {guide.koreanCommunity.churches}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <GraduationCap className="w-5 h-5 text-blue-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Schools</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {guide.koreanCommunity.schools}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <ShoppingCart className="w-5 h-5 text-coral-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Korean Markets</span>
                  </div>
                  <p className="text-sm text-gray-600 dark:text-gray-300">
                    {guide.koreanCommunity.markets}
                  </p>
                </div>
                <div className="p-4 bg-gray-50 dark:bg-slate-700/50 rounded-lg">
                  <div className="flex items-center gap-2 mb-2">
                    <Building2 className="w-5 h-5 text-emerald-500" />
                    <span className="font-medium text-gray-900 dark:text-white">Organizations</span>
                  </div>
                  <ul className="text-sm text-gray-600 dark:text-gray-300 space-y-1">
                    {guide.koreanCommunity.organizations.map((org, idx) => (
                      <li key={idx}>• {org}</li>
                    ))}
                  </ul>
                </div>
              </div>
            </section>

            {/* Living Tips */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <Lightbulb className="w-5 h-5 text-amber-500" />
                Living Tips
              </h2>
              <ul className="space-y-3">
                {guide.livingTips.map((tip, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-3 text-gray-600 dark:text-gray-300"
                  >
                    <span className="w-6 h-6 bg-emerald-100 dark:bg-emerald-900/30 text-emerald-600 dark:text-emerald-400 rounded-full flex items-center justify-center flex-shrink-0 text-sm font-medium">
                      {idx + 1}
                    </span>
                    {tip}
                  </li>
                ))}
              </ul>
            </section>

            {/* Challenges */}
            <section className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700">
              <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-amber-500" />
                Challenges to Consider
              </h2>
              <ul className="space-y-2">
                {guide.challenges.map((challenge, idx) => (
                  <li
                    key={idx}
                    className="flex items-start gap-2 text-gray-600 dark:text-gray-300"
                  >
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    {challenge}
                  </li>
                ))}
              </ul>
            </section>
          </div>

          {/* Sidebar */}
          <div className="space-y-6">
            {/* Practical Info */}
            <div className="bg-white dark:bg-slate-800 rounded-xl p-6 border border-gray-200 dark:border-slate-700 sticky top-4">
              <h3 className="font-semibold text-gray-900 dark:text-white mb-4">
                Practical Information
              </h3>
              <div className="space-y-3 text-sm">
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Languages className="w-4 h-4" />
                    Language
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.language}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <CreditCard className="w-4 h-4" />
                    Currency
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.currency}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Clock className="w-4 h-4" />
                    Timezone
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.timezone}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Car className="w-4 h-4" />
                    Driving Side
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.drivingSide}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Plug className="w-4 h-4" />
                    Electric Plug
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.electricPlug}</span>
                </div>
                <div className="flex items-center justify-between">
                  <span className="flex items-center gap-2 text-gray-500 dark:text-gray-400">
                    <Phone className="w-4 h-4" />
                    Emergency
                  </span>
                  <span className="text-gray-900 dark:text-white">{guide.practicalInfo.emergencyNumber}</span>
                </div>
              </div>

              {/* Quick Links */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Quick Links</h4>
                <div className="space-y-2">
                  <Link
                    to={`/immigration?tab=guide&country=${guide.code}`}
                    className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
                  >
                    <Stethoscope className="w-4 h-4" />
                    Visa Information
                  </Link>
                  <Link
                    to={'/guides?category=life'}
                    className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
                  >
                    <Users className="w-4 h-4" />
                    Community Posts
                  </Link>
                  {/* Stays is retired — point at the editorial guides hub instead. */}
                  <Link
                    to="/guides"
                    className="flex items-center gap-2 text-emerald-600 dark:text-emerald-400 hover:underline text-sm"
                  >
                    <Home className="w-4 h-4" />
                    Settlement Guides
                  </Link>
                </div>
              </div>

              {/* Resources */}
              <div className="mt-6 pt-6 border-t border-gray-200 dark:border-slate-700">
                <h4 className="font-medium text-gray-900 dark:text-white mb-3">Official Resources</h4>
                <div className="space-y-2">
                  {guide.resources.map((resource, idx) => (
                    <a
                      key={idx}
                      href={resource.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="flex items-start gap-2 text-sm group"
                    >
                      <ExternalLink className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
                      <div>
                        <span className="text-emerald-600 dark:text-emerald-400 group-hover:underline">
                          {resource.name}
                        </span>
                        <p className="text-gray-500 dark:text-gray-400 text-xs">
                          {resource.description}
                        </p>
                      </div>
                    </a>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
